import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';

@Injectable({ providedIn: 'root' })
export class InactivityService implements OnDestroy {

	private readonly authContextKey = 'authContext';
	private readonly defaultAuthContext = 'login';
	private readonly companyAuthContext = 'companyAuth';
	private readonly collegeAuthContext = 'collegeAuth';
	private timeoutId: any;
	private timeoutDuration = 7 * 24 * 60 * 60 * 1000;
	private activityHandler = () => this.resetTimer();

	constructor(
		private router: Router,
		private _toastr: ToastrService,
		private zone: NgZone) {
		this.setAuthContextFromUrl(window.location.pathname + window.location.search);
		this.initListener();

		window.addEventListener('storage', (event) => {
			if (event.key === 'logout-event') {
				this.zone.run(() => this.handleLogout(false));
			}
		});
	}

	initListener() {
		const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'click', 'touchstart',];
		events.forEach((event) => window.addEventListener(event, this.activityHandler, { passive: true }));
		this.resetTimer();
	}


	setAuthContextFromUrl(url: string): void {
		const path = url.split('?')[0];
		const isCompanyRoute = [
			'/company-auth',
			'/recruiter-auth',
			'/recruiter-dashboard',
			'/company-enrollment-test',
			'/company-status'
		].some(route => path === route || path.startsWith(`${route}/`));

		const isCollegeRoute = [
			'/college-auth',
			'/college-dashboard'
		].some(route => path === route || path.startsWith(`${route}/`));

		if (isCompanyRoute) {
			sessionStorage.setItem(this.authContextKey, this.companyAuthContext);
		} else if (isCollegeRoute) {
			sessionStorage.setItem(this.authContextKey, this.collegeAuthContext);
		} else if (path === '/login' || !sessionStorage.getItem(this.authContextKey)) {
			sessionStorage.setItem(this.authContextKey, this.defaultAuthContext);
		}
	}


	getLoginRoute(): string {
		this.setAuthContextFromUrl(window.location.pathname + window.location.search);
		const context = sessionStorage.getItem(this.authContextKey);
		if (context === this.companyAuthContext) return '/company-auth';
		if (context === this.collegeAuthContext) return '/college-auth';
		return '/login';
	}


	private resetTimer() {
		clearTimeout(this.timeoutId);

		this.timeoutId = setTimeout(() => {
			this.handleLogout(true);
		}, this.timeoutDuration);
	}

	private handleLogout(triggerBroadcast: boolean) {

		const keysToRemove = ['token', 'refresh_token', 'email', 'role_id', 'user_id', 'ai_quiz_data',
			'user_time_zone', 'tenant_id', 'onesignal_player_id', 'modules', 'fcmToken', 'country', 'last_active_user_id',
			'job_id', 'phone_email', 'fcmTokenTimestamp', 'company_id'];

		keysToRemove.forEach(key => localStorage.removeItem(key));
		Object.keys(localStorage).forEach(key => {
			if (key.endsWith('_ai_quiz_data')) {
				localStorage.removeItem(key);
			}
		});

		if (triggerBroadcast) {
			localStorage.setItem('logout-event', Date.now().toString());
		}

		this.zone.run(() => {
			this._toastr.warning('You have been logged out due to inactivity.', 'Session Expired');
			this.router.navigate([this.getLoginRoute()], { queryParams: { sessionExpired: true } });
		});
	}

	checkForDifferentUser(newUserId: string) {
		localStorage.setItem('user_id', newUserId);
	}

	clearUserData() {
		localStorage.clear();
	}

	stop() {
		clearTimeout(this.timeoutId);
		this.removeListeners();
	}

	private removeListeners() {
		const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'click', 'touchstart'];
		events.forEach((event) => window.removeEventListener(event, this.activityHandler));
	}

	ngOnDestroy(): void {
		this.stop();
	}
}
